import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransactions } from '../slices/transactionSlice';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/api';
import categoryService from '../services/categoryService';
import goldColors from '../themes/goldColors';
import { getCategoryEmoji, buildEmojiOverrides } from '../utils/categoryEmoji';
// Iconos
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

// 25 ago 2026 (Task #10 -- pedido de Augusto: "Crea un lugar aparte para
// Categorias, donde se puedan modificar y corregir"): esta página junta en
// un solo lugar las 7 categorías base (fijas, hardcodeadas en toda la app
// -- ver getCategories en StatementImporter.js/TransactionsPage.js) y las
// categorías que el usuario ya usa de verdad, sea porque las creó a mano
// (tabla Category, endpoints/categories.py) o porque quedaron guardadas
// como texto libre en Transaction.categoria (WhatsApp, voz, importador de
// estados de cuenta -- nunca pasan por la tabla Category). Reutiliza el
// mismo endpoint de fusión que ya existe para transacciones
// (/transactions/renombrar-categoria) para poder renombrar o fusionar
// cualquiera de las dos, y agrega borrar para las personalizadas.

// Mismo mapa que _TRADUCCION_CATEGORIA_BASE en merchant_rules.py (backend)
// -- debe mantenerse igual si alguna de las dos listas cambia. Traduce las
// claves base en inglés que guardan las importaciones con IA a su forma
// canónica en español, para que "food" y "Alimentación" se reconozcan como
// la misma categoría acá también.
const TRADUCCION_CATEGORIA_BASE = {
  food: 'Alimentación',
  transport: 'Transporte',
  bills: 'Facturas y Servicios',
  shopping: 'Compras',
  entertainment: 'Entretenimiento',
  salary: 'Salario',
  other: 'Otro',
  otro: 'Otro',
};
const traducirCategoriaBase = (nombre) => {
  const clave = (nombre || '').trim().toLowerCase();
  return TRADUCCION_CATEGORIA_BASE[clave] || nombre;
};

// Misma normalización que TransactionsPage.js/merchant_rules.py -- unifica
// mayúsculas, tildes, y forma Unicode (NFC vs NFD), usando escapes \uXXXX
// puros en vez de caracteres Unicode literales en el regex (ver nota del
// 20 ago 2026 en TransactionsPage.js: los caracteres combinantes literales
// son frágiles al copiar/pegar y pueden corromperse en silencio).
const normalizarCategoria = (s) => {
  if (!s) return '';
  const nfc = s.normalize('NFC');
  const sinTildes = nfc.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const sinInvisibles = sinTildes.replace(/[\u200b\u200c\u200d\ufeff\u00ad]/g, '');
  return sinInvisibles.trim().toLowerCase().replace(/\s+/g, ' ');
};

const BASE_CATEGORY_NAMES_ES = [
  'Alimentación',
  'Transporte',
  'Facturas y Servicios',
  'Compras',
  'Entretenimiento',
  'Salario',
  'Otro',
];

// 28 ago 2026 (pedido de Augusto: "Categorias no te da la opcion de
// cambiar el emoji"): set curado de emojis comunes para categorías de
// gastos/ingresos -- se muestran como grilla clickeable en el diálogo de
// abajo. El campo de texto al final del diálogo sigue permitiendo pegar
// cualquier otro emoji (o varios caracteres) que no esté en esta lista.
// 28 ago 2026 (pedido de Augusto: "la selección es muy reducida, no
// puedes poner muchísimos más?"): se pasa de una lista plana de 40 a
// ~110 emojis, agrupados por tema (ver EMOJI_SECCIONES más abajo) para
// que encontrar uno puntual entre tantos siga siendo rápido en vez de
// tener que escanear una sola grilla gigante sin orden.
const EMOJI_SECCIONES = [
  {
    titulo: 'Comida y bebida',
    emojis: ['🍔', '🍕', '🌮', '🍜', '🍣', '🥗', '🍰', '🍿', '☕', '🍺', '🍷', '🧃', '🛒', '🍳', '🥐'],
  },
  {
    titulo: 'Transporte',
    emojis: ['🚗', '🚕', '🚌', '🚇', '🚲', '🛵', '⛽', '🅿️', '✈️', '🚢', '🚆', '🛺', '🚦', '🧭'],
  },
  {
    titulo: 'Hogar y servicios',
    emojis: ['🏠', '🏢', '💡', '💧', '🔥', '📶', '📺', '☎️', '🧹', '🔧', '🛋️', '🪑', '🧯', '🧺'],
  },
  {
    titulo: 'Compras',
    emojis: ['🛍️', '👕', '👗', '👟', '👜', '💄', '🕶️', '⌚', '💍', '🎁', '🧴', '🧸'],
  },
  {
    titulo: 'Entretenimiento',
    emojis: ['🎬', '🎮', '🎵', '🎤', '🎨', '🎭', '🎟️', '📺', '🎉', '🃏', '📷', '🎧'],
  },
  {
    titulo: 'Finanzas',
    emojis: ['💰', '💵', '💳', '🏦', '📈', '📉', '🪙', '💹', '🧾', '💸', '🏧', '📊'],
  },
  {
    titulo: 'Salud',
    emojis: ['⚕️', '💊', '🏥', '🦷', '👓', '🩺', '🧠', '💉', '🧘', '🩹'],
  },
  {
    titulo: 'Educación',
    emojis: ['📚', '🎓', '✏️', '🖥️', '🏫', '🧮', '📝', '🔬'],
  },
  {
    titulo: 'Viajes',
    emojis: ['✈️', '🏖️', '🗺️', '🏨', '🧳', '🌴', '⛰️', '🚤', '🗽', '🏕️'],
  },
  {
    titulo: 'Mascotas y deporte',
    emojis: ['🐾', '🐶', '🐱', '⚽', '🏋️', '🚴', '🏊', '🎾', '🏀', '🥊'],
  },
  {
    titulo: 'Familia y trabajo',
    emojis: ['👨‍👩‍👧', '👶', '💼', '🧑‍💻', '📞', '✉️', '👴', '🎂'],
  },
  {
    titulo: 'Otros',
    emojis: ['📦', '⭐', '🏷️', '🔖', '📌', '🧩', '🔷', '💠', '❓', '✅', '🚬', '🧴'],
  },
];

const CategoriesPage = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const dispatch = useDispatch();
  const transactions = useSelector((state) => state.transactions.list);

  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const [newCategoryName, setNewCategoryName] = useState('');
  const [creating, setCreating] = useState(false);

  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeOrigen, setMergeOrigen] = useState('');
  const [mergeDestino, setMergeDestino] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // 28 ago 2026 (pedido de Augusto): estado del diálogo de "elegir
  // emoji" -- `emojiTarget` guarda la categoría (nombre + emoji actual)
  // sobre la que se está eligiendo; `emojiCustom` es el texto del campo
  // libre, separado de la grilla de sugeridos para que uno no le gane al
  // otro visualmente mientras el usuario escribe.
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiCustom, setEmojiCustom] = useState('');
  const [emojiSaving, setEmojiSaving] = useState(false);
  const [emojiError, setEmojiError] = useState(null);

  const cargarDatos = () => {
    setLoading(true);
    setError(null);
    Promise.all([categoryService.getAll(), dispatch(fetchTransactions())])
      .then(([catRes]) => {
        setCustomCategories(catRes.data || []);
      })
      .catch(() => {
        setError(
          isSpanish
            ? 'No se pudieron cargar las categorías. Intenta recargar la página.'
            : 'Could not load categories. Try reloading the page.'
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 28 ago 2026: mapa { nombreNormalizado: emoji } armado a partir de las
  // categorías que ya tienen un emoji propio guardado (ver
  // utils/categoryEmoji.js) -- se usa tanto para PINTAR el emoji correcto
  // de cada fila como para precargar el diálogo de edición.
  const emojiOverrides = useMemo(() => buildEmojiOverrides(customCategories), [customCategories]);

  // Junta las 7 categorías base + las personalizadas guardadas (tabla
  // Category) + cualquier categoría que aparezca de verdad en
  // transacciones pero que nunca haya pasado por la tabla Category (texto
  // libre de WhatsApp/voz/importador). Deduplica todo por la misma
  // normalización que usa el resto de la app.
  const categoriasEnUso = useMemo(() => {
    const mapa = new Map(); // normKey -> { nombre, count, esBase, categoryId, emoji }

    BASE_CATEGORY_NAMES_ES.forEach((nombre) => {
      mapa.set(normalizarCategoria(nombre), { nombre, count: 0, esBase: true, categoryId: null, emoji: null });
    });

    customCategories.forEach((c) => {
      const norm = normalizarCategoria(c.nombre);
      if (mapa.has(norm)) {
        const existente = mapa.get(norm);
        existente.categoryId = c.id;
        existente.emoji = c.emoji || null;
      } else {
        mapa.set(norm, { nombre: c.nombre, count: 0, esBase: false, categoryId: c.id, emoji: c.emoji || null });
      }
    });

    (transactions || []).forEach((t) => {
      if (!t.categoria) return;
      const nombreTraducido = traducirCategoriaBase(t.categoria);
      const norm = normalizarCategoria(nombreTraducido);
      if (mapa.has(norm)) {
        mapa.get(norm).count += 1;
      } else {
        mapa.set(norm, { nombre: nombreTraducido, count: 1, esBase: false, categoryId: null, emoji: null });
      }
    });

    // 3 sep 2026 (Augusto: "las categorías necesitan estar en orden"):
    // antes se ordenaba por cantidad de transacciones (la más usada
    // primero) -- con localeCompare como único desempate. Augusto pidió
    // alfabético a secas, para poder escanear la lista y encontrar una
    // categoría puntual sin importar cuánto se use. 'es' + sensitivity
    // 'base' ordena "Alimentación" junto a "alquiler" sin que
    // mayúsculas/tildes rompan el orden esperado.
    return Array.from(mapa.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
    );
  }, [customCategories, transactions]);

  const categoriasFiltradas = useMemo(() => {
    const q = normalizarCategoria(search);
    if (!q) return categoriasEnUso;
    return categoriasEnUso.filter((c) => normalizarCategoria(c.nombre).includes(q));
  }, [categoriasEnUso, search]);

  const todosLosNombres = useMemo(() => categoriasEnUso.map((c) => c.nombre), [categoriasEnUso]);

  const handleCreateCategory = async () => {
    const nombre = newCategoryName.trim();
    if (!nombre) return;
    setCreating(true);
    setError(null);
    try {
      await categoryService.create(nombre);
      setNewCategoryName('');
      cargarDatos();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo crear la categoría.' : 'Could not create the category.')
      );
    } finally {
      setCreating(false);
    }
  };

  // 25 ago 2026 (pedido de Augusto: "no permite renombrar, solo permite
  // fusionar"): el diálogo ya soportaba renombrar de verdad (si "destino"
  // no coincide con ninguna categoría existente, el backend simplemente la
  // renombra -- ver renombrar_categoria en endpoints/transactions.py), pero
  // se sentía como una pantalla de "fusionar con otra" porque el campo
  // Destino arrancaba vacío y sin relación visible con el nombre actual.
  // Ahora arranca YA precargado con el nombre actual -- renombrar es tan
  // simple como editar ese texto; fusionar sigue siendo posible si en vez
  // de eso se escribe el nombre de una categoría que ya existe.
  const handleOpenMergeDialog = (nombreOrigen) => {
    setMergeOrigen(nombreOrigen);
    setMergeDestino(nombreOrigen);
    setMergeError(null);
    setMergeResult(null);
    setMergeDialogOpen(true);
  };
  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false);
  };
  const handleConfirmMerge = async () => {
    const destino = mergeDestino.trim();
    if (!mergeOrigen || !destino) return;
    setMergeLoading(true);
    setMergeError(null);
    try {
      const { data } = await apiClient.post('/transactions/renombrar-categoria', {
        origen: mergeOrigen,
        destino,
      });
      setMergeResult(data);
      cargarDatos();
      // 3 sep 2026 (Augusto: "cuando cambio una categoría el formulario no
      // se cierra despues de confirmar el cambio"): antes, un renombrar/
      // fusionar exitoso dejaba mergeDialogOpen en true -- solo se
      // guardaba mergeResult y había que cerrar el diálogo a mano con el
      // botón "Cerrar", a diferencia de los otros dos diálogos de esta
      // misma página (emoji, borrar) que sí se cierran solos al terminar.
      // Ahora hace lo mismo: se cierra apenas el cambio se confirma.
      handleCloseMergeDialog();
    } catch (err) {
      setMergeError(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo renombrar/fusionar la categoría.' : 'Could not rename/merge the category.')
      );
    } finally {
      setMergeLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.categoryId) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/categories/${deleteTarget.categoryId}`);
      setDeleteTarget(null);
      cargarDatos();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo eliminar la categoría.' : 'Could not delete the category.')
      );
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // 28 ago 2026 (pedido de Augusto: "Categorias no te da la opcion de
  // cambiar el emoji"): abre el diálogo con el emoji ACTUAL de esa
  // categoría ya seleccionado (el guardado si tiene, si no el automático
  // por palabra clave -- ver getCategoryEmoji) para que el usuario vea de
  // dónde parte antes de cambiarlo.
  const handleOpenEmojiDialog = (cat) => {
    setEmojiTarget(cat);
    setEmojiCustom(cat.emoji || '');
    setEmojiError(null);
  };
  const handleCloseEmojiDialog = () => {
    setEmojiTarget(null);
    setEmojiCustom('');
    setEmojiError(null);
  };
  // Reutiliza el mismo endpoint idempotente que "Crear categoría"
  // (POST /categories, ver endpoints/categories.py) -- ahora también
  // acepta `emoji` y actualiza la fila si ya existe, o la crea recién
  // acá si es la primera vez que una categoría BASE (que nunca tuvo fila
  // propia) recibe un emoji personalizado.
  const handleSaveEmoji = async (emojiElegido) => {
    if (!emojiTarget || !emojiElegido || !emojiElegido.trim()) return;
    setEmojiSaving(true);
    setEmojiError(null);
    try {
      await apiClient.post('/categories', { nombre: emojiTarget.nombre, emoji: emojiElegido.trim() });
      handleCloseEmojiDialog();
      cargarDatos();
    } catch (err) {
      setEmojiError(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo guardar el emoji.' : 'Could not save the emoji.')
      );
    } finally {
      setEmojiSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isSpanish
          ? 'Todas las categorías que usas hoy, incluidas las 7 base de la app y las que hayas creado tú mismo o que se hayan guardado desde WhatsApp, voz, o al importar un estado de cuenta. Renombra o fusiona las que estén repetidas, cambia su emoji, y elimina las personalizadas que ya no uses.'
          : "All the categories you use today, including the app's 7 base categories and any you created yourself or that got saved from WhatsApp, voice, or a statement import. Rename or merge duplicates, change their emoji, and delete custom ones you no longer use."}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {isSpanish ? 'Crear una categoría nueva' : 'Create a new category'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={isSpanish ? 'Ej. Mascotas, Hobbies…' : 'E.g. Pets, Hobbies…'}
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateCategory();
              }
            }}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            disabled={creating || !newCategoryName.trim()}
            onClick={handleCreateCategory}
            sx={{ bgcolor: goldColors.main, '&:hover': { bgcolor: goldColors.dark } }}
          >
            {isSpanish ? 'Crear' : 'Create'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {isSpanish
            ? 'Si ya existe una parecida (mayúsculas, tildes, o un error de tipeo), se reconoce automáticamente en vez de crear una duplicada.'
            : "If a similar one already exists (case, accents, or a typo), it's recognized automatically instead of creating a duplicate."}
        </Typography>
      </Paper>

      <TextField
        size="small"
        fullWidth
        placeholder={isSpanish ? 'Buscar categoría…' : 'Search category…'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: goldColors.main }} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableCell>{isSpanish ? 'Categoría' : 'Category'}</TableCell>
                <TableCell>{isSpanish ? 'Origen' : 'Source'}</TableCell>
                <TableCell align="right">{isSpanish ? 'Transacciones' : 'Transactions'}</TableCell>
                <TableCell align="right">{isSpanish ? 'Acciones' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoriasFiltradas.map((cat) => (
                <TableRow key={`${cat.nombre}-${cat.categoryId ?? 'base'}`} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* 28 ago 2026 (pedido de Augusto: "Categorias no te
                          da la opcion de cambiar el emoji"): el emoji (el
                          guardado, o el automático si no tiene uno propio
                          -- ver getCategoryEmoji) ahora es un botón aparte
                          que abre el selector, separado del nombre (que
                          sigue abriendo renombrar/fusionar como antes). */}
                      <Tooltip title={isSpanish ? 'Cambiar emoji' : 'Change emoji'}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEmojiDialog(cat)}
                          sx={{ fontSize: '1.1rem', p: 0.5 }}
                        >
                          {getCategoryEmoji(cat.nombre, emojiOverrides)}
                        </IconButton>
                      </Tooltip>
                      {/* 25 ago 2026 (pedido de Augusto): el nombre se veía
                          clickeable (heredaba un color tipo link) pero no
                          hacía nada -- ahora sí abre el mismo diálogo de
                          renombrar/fusionar que el ícono de Acciones, ya
                          precargado con este nombre. */}
                      <Box
                        onClick={() => handleOpenMergeDialog(cat.nombre)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {cat.nombre}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        cat.esBase
                          ? isSpanish ? 'Base' : 'Base'
                          : isSpanish ? 'Personalizada' : 'Custom'
                      }
                      color={cat.esBase ? 'default' : 'primary'}
                      variant={cat.esBase ? 'outlined' : 'filled'}
                      sx={!cat.esBase ? { bgcolor: alpha(goldColors.main, 0.15), color: goldColors.dark } : undefined}
                    />
                  </TableCell>
                  <TableCell align="right">{cat.count}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={isSpanish ? 'Renombrar / fusionar' : 'Rename / merge'}>
                      <IconButton size="small" onClick={() => handleOpenMergeDialog(cat.nombre)}>
                        <MergeTypeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        cat.esBase
                          ? isSpanish
                            ? 'Las 7 categorías base no se pueden eliminar.'
                            : "The 7 base categories can't be deleted."
                          : isSpanish
                            ? 'Eliminar categoría personalizada'
                            : 'Delete custom category'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={cat.esBase || !cat.categoryId}
                          onClick={() => setDeleteTarget(cat)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {categoriasFiltradas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {isSpanish ? 'No se encontraron categorías.' : 'No categories found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo de renombrar/fusionar -- mismo endpoint que ya usa
          TransactionsPage.js (POST /transactions/renombrar-categoria):
          mueve transacciones, reglas de proveedor, y la fila de Category
          (si existe) de "origen" a "destino". Si "destino" no existe
          todavía como categoría propia, se crea. */}
      <Dialog open={mergeDialogOpen} onClose={handleCloseMergeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isSpanish ? 'Renombrar / fusionar categoría' : 'Rename / merge category'}
          <IconButton
            aria-label="close"
            onClick={handleCloseMergeDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isSpanish
              ? 'Edita el nombre de abajo para renombrar esta categoría. Si en vez de eso escribes el nombre de una categoría que ya existe, se fusionan en una sola.'
              : "Edit the name below to rename this category. If you instead type the name of a category that already exists, they'll be merged into one."}
          </Typography>
          <TextField
            label={isSpanish ? 'Categoría actual' : 'Current category'}
            value={mergeOrigen}
            fullWidth
            disabled
            sx={{ mb: 2 }}
          />
          <Autocomplete
            freeSolo
            options={todosLosNombres}
            inputValue={mergeDestino}
            onInputChange={(e, value) => setMergeDestino(value)}
            renderInput={(params) => (
              <TextField {...params} label={isSpanish ? 'Nuevo nombre' : 'New name'} placeholder={isSpanish ? 'Ej. Alimentación' : 'E.g. Food & Dining'} />
            )}
          />
          {mergeDestino.trim() && normalizarCategoria(mergeDestino) !== normalizarCategoria(mergeOrigen) && todosLosNombres.some((n) => normalizarCategoria(n) === normalizarCategoria(mergeDestino)) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {isSpanish
                ? `"${mergeDestino}" ya existe -- se fusionarán ambas categorías en una sola.`
                : `"${mergeDestino}" already exists -- both categories will be merged into one.`}
            </Typography>
          )}
          {mergeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mergeError}
            </Alert>
          )}
          {mergeResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {isSpanish
                ? `Transacciones actualizadas: ${mergeResult.transacciones_actualizadas} · Reglas de proveedor actualizadas: ${mergeResult.reglas_proveedor_actualizadas} · Categorías personalizadas fusionadas: ${mergeResult.categorias_personalizadas_eliminadas} · Presupuestos actualizados: ${mergeResult.presupuestos_actualizados ?? 0}${mergeResult.presupuestos_eliminados ? ` (${mergeResult.presupuestos_eliminados} fusionados con uno ya existente)` : ''}`
                : `Transactions updated: ${mergeResult.transacciones_actualizadas} · Merchant rules updated: ${mergeResult.reglas_proveedor_actualizadas} · Custom categories merged: ${mergeResult.categorias_personalizadas_eliminadas} · Budgets updated: ${mergeResult.presupuestos_actualizados ?? 0}${mergeResult.presupuestos_eliminados ? ` (${mergeResult.presupuestos_eliminados} merged into an existing one)` : ''}`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMergeDialog}>{isSpanish ? 'Cerrar' : 'Close'}</Button>
          <Button
            variant="contained"
            disabled={
              mergeLoading ||
              !mergeDestino.trim() ||
              // 25 ago 2026 (bug encontrado por Augusto: "suscripciones" ->
              // "Suscripciones" dejaba el botón deshabilitado): la
              // comparación normalizada (normalizarCategoria) ignora
              // mayúsculas/tildes a propósito para DETECTAR fusiones, pero
              // usarla acá también bloqueaba corregir solo la
              // capitalización -- un cambio real y válido. Comparar el
              // texto exacto (trim) es lo correcto para decidir si "no se
              // cambió nada"; la detección de fusión (con normalizarCategoria)
              // sigue aparte, en el aviso de arriba.
              mergeDestino.trim() === mergeOrigen.trim()
            }
            startIcon={mergeLoading ? <CircularProgress size={16} color="inherit" /> : <MergeTypeIcon />}
            onClick={handleConfirmMerge}
            sx={{ bgcolor: goldColors.main, '&:hover': { bgcolor: goldColors.dark } }}
          >
            {isSpanish ? 'Confirmar' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 28 ago 2026 (pedido de Augusto: "Categorias no te da la opcion
          de cambiar el emoji"): grilla de emojis sugeridos + campo libre
          para pegar cualquier otro. Clic en un emoji de la grilla lo
          guarda al toque (sin botón "Confirmar" aparte); el campo de
          texto de abajo es para cuando ninguno de los sugeridos sirve. */}
      <Dialog open={!!emojiTarget} onClose={handleCloseEmojiDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isSpanish ? 'Elegir emoji' : 'Choose emoji'}
          {emojiTarget && (
            <Typography variant="body2" color="text.secondary">
              {emojiTarget.nombre}
            </Typography>
          )}
          <IconButton
            aria-label="close"
            onClick={handleCloseEmojiDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 420 }}>
          {EMOJI_SECCIONES.map((seccion) => (
            <Box key={seccion.titulo} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                {seccion.titulo}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {seccion.emojis.map((emo) => (
                  <IconButton
                    key={emo}
                    onClick={() => handleSaveEmoji(emo)}
                    disabled={emojiSaving}
                    size="small"
                    sx={{
                      fontSize: '1.2rem',
                      border: '1px solid',
                      borderColor: emojiTarget?.emoji === emo ? goldColors.main : 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    {emo}
                  </IconButton>
                ))}
              </Box>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, mt: 1 }}>
            {isSpanish
              ? 'O escribe/pega cualquier otro emoji (en Windows: clic en el cuadro y Win + . para abrir el selector de emojis):'
              : 'Or type/paste any other emoji (on Windows: click the box and press Win + . to open the emoji picker):'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              value={emojiCustom}
              onChange={(e) => setEmojiCustom(e.target.value)}
              placeholder="🎯"
              sx={{ width: 100 }}
              inputProps={{ style: { fontSize: '1.2rem', textAlign: 'center' } }}
            />
            <Button
              variant="contained"
              disabled={emojiSaving || !emojiCustom.trim()}
              startIcon={emojiSaving ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={() => handleSaveEmoji(emojiCustom)}
              sx={{ bgcolor: goldColors.main, '&:hover': { bgcolor: goldColors.dark } }}
            >
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </Box>
          {emojiError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {emojiError}
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmación de borrado -- solo aplica a categorías personalizadas
          (tabla Category). Borrar la fila de Category no borra ni
          modifica las transacciones que ya usan ese texto -- solo deja de
          aparecer como sugerencia guardada al crear/editar. */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{isSpanish ? 'Eliminar categoría' : 'Delete category'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isSpanish
              ? `¿Eliminar "${deleteTarget?.nombre}"?`
              : `Delete "${deleteTarget?.nombre}"?`}
          </Typography>
          {deleteTarget?.count > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {isSpanish
                ? `Tiene ${deleteTarget.count} transacción(es) asociadas. No se van a borrar ni a cambiar de categoría -- solo dejará de aparecer como categoría guardada.`
                : `It has ${deleteTarget.count} associated transaction(s). They won't be deleted or recategorized -- it will just stop appearing as a saved category.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{isSpanish ? 'Cancelar' : 'Cancel'}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={handleConfirmDelete}
          >
            {isSpanish ? 'Eliminar' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;
